// sanitize.js — Input sanitization utilities for FirmAssist
// Prevents XSS, script injection, and cleans user inputs before storage

/**
 * Sanitize a string — removes tag-forming characters and dangerous HTML delimiters.
 *
 * IMPORTANT: We escape <, >, and " because those can break out of text nodes or
 * attribute values if the output is ever rendered as HTML. We do NOT escape
 * forward slashes or apostrophes because:
 *   - React auto-escapes all interpolated strings in JSX, so tags cannot form
 *     from / or ' in rendered content
 *   - Ghanaian case numbers legitimately contain slashes (C5/158/2024,
 *     J4/68/2023, H1/12/2024) — escaping them corrupts real data
 *   - Names like D'Arcy and common words like "don't" contain apostrophes
 *   - Dates (dd/mm/yyyy), URLs, file paths all require unescaped slashes
 * For the one surface that renders user HTML directly (receipt printing),
 * sanitizeHTML below does tag-aware cleaning that makes per-character slash
 * escaping unnecessary.
 */
export const sanitizeText = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .trim();
};

/**
 * Sanitize for display — lighter version that just strips HTML tags
 * Use when rendering user-generated content
 */
export const sanitizeForDisplay = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/<[^>]*>/g, '').trim();
};

/**
 * Sanitize an object — recursively sanitize all string values
 * Use on form data objects before saving to Firestore
 */
export const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      cleaned[key] = sanitizeText(value);
    } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
      cleaned[key] = sanitizeObject(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

/**
 * Sanitize email — validate and clean email addresses
 */
export const sanitizeEmail = (email) => {
  if (typeof email !== 'string') return '';
  const cleaned = email.toLowerCase().trim();
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(cleaned) ? cleaned : '';
};

/**
 * Sanitize phone — keep only digits, +, -, (, ), and spaces
 */
export const sanitizePhone = (phone) => {
  if (typeof phone !== 'string') return '';
  return phone.replace(/[^\d+\-() ]/g, '').trim();
};

/**
 * Sanitize currency amount — ensure it's a valid positive number
 */
export const sanitizeAmount = (amount) => {
  const num = parseFloat(String(amount).replace(/[^0-9.]/g, ''));
  return isNaN(num) || num < 0 ? 0 : Math.round(num * 100) / 100;
};

/**
 * Truncate string to max length
 */
export const truncate = (input, maxLength = 500) => {
  if (typeof input !== 'string') return input;
  return input.length > maxLength ? input.substring(0, maxLength) + '...' : input;
};

/**
 * Sanitize HTML for receipt/print — allows only safe tags
 * Use for the receipt innerHTML 
 */
export const sanitizeHTML = (html) => {
  if (typeof html !== 'string') return '';
  // Remove script tags, event handlers, and dangerous attributes
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/data\s*:/gi, 'blocked:')
    .replace(/vbscript\s*:/gi, '');
};
